import { Activity, Bot, Play, RefreshCw, ShieldCheck, SlidersHorizontal, Sparkles, Trash2 } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  useClearCapturedContent,
  useRunSource,
  useSourceOperations,
  useSyncContentScraperSource,
  useUpdateSourceOperations,
} from "@/hooks/useSources";
import { useInboxBatchExtraction } from "@/hooks/useInboxExtraction";
import {
  ApiError,
  type ScrapePolicy,
  type SourceConfig,
  type SourceOperations,
  type SourceProcessing,
  type SourceRuntime,
  type SourceRuntimeMode,
} from "@/lib/api";

interface Props {
  source: SourceConfig;
  triggerClassName?: string;
}

export function SourceOperationsSheet({ source, triggerClassName }: Props) {
  const [open, setOpen] = useState(false);
  const operationsQ = useSourceOperations(source.id, open);
  const updateOperations = useUpdateSourceOperations();
  const syncWorker = useSyncContentScraperSource();
  const clearCaptured = useClearCapturedContent();
  const runSource = useRunSource();
  const analyzeSource = useInboxBatchExtraction();
  const [cadence, setCadence] = useState(source.cadence);
  const [runtimeMode, setRuntimeMode] = useState<SourceRuntimeMode>("poll");
  const [runtimeConfig, setRuntimeConfig] = useState<SourceRuntime["config"]>({});
  const [policy, setPolicy] = useState<ScrapePolicy | null>(null);
  const [processing, setProcessing] = useState<SourceProcessing | null>(null);

  useEffect(() => {
    if (operationsQ.data) {
      setCadence(operationsQ.data.cadence);
      setRuntimeMode(operationsQ.data.runtime.mode);
      setRuntimeConfig(operationsQ.data.runtime.config);
      setPolicy(operationsQ.data.scrape_policy);
      setProcessing(operationsQ.data.processing);
    }
  }, [operationsQ.data]);

  const save = async () => {
    if (!policy || !processing) return;
    try {
      await updateOperations.mutateAsync({
        sourceId: source.id,
        patch: {
          cadence,
          runtime_mode: runtimeMode,
          runtime_config: runtimeConfig,
          scrape_policy: policy,
          processing,
        },
      });
      toast.success(`saved operations for ${source.id}`);
    } catch (err) {
      toast.error(`save failed: ${(err as Error).message}`);
    }
  };

  const clear = async () => {
    const confirmed = window.confirm(
      `Delete captured inbox content for ${source.display_name}?`,
    );
    if (!confirmed) return;
    try {
      const resp = await clearCaptured.mutateAsync(source.id);
      toast.success(
        `cleared ${resp.inbox_items_deleted} captured item${resp.inbox_items_deleted === 1 ? "" : "s"}`,
      );
    } catch (err) {
      toast.error(`clear failed: ${(err as Error).message}`);
    }
  };

  const sync = async () => {
    try {
      const resp = await syncWorker.mutateAsync(source.id);
      toast.success(resp.message);
    } catch (err) {
      toast.error(`sync failed: ${(err as Error).message}`);
    }
  };

  const runNow = async () => {
    try {
      const resp = await runSource.mutateAsync(source.id);
      const itemLabel = `captured ${resp.inbox_items_created} item${resp.inbox_items_created === 1 ? "" : "s"}`;
      const processingLabel =
        resp.processing_status === "skipped"
          ? "processing skipped"
          : `processing ${resp.processing_status}: ${resp.processing_converted_count} converted`;
      toast.success(`${itemLabel}; ${processingLabel}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.info(`${source.display_name} is already running.`);
      } else {
        toast.error(`run failed: ${(err as Error).message}`);
      }
    }
  };

  const analyzeExisting = () => {
    if (!processing) return;
    analyzeSource.mutate({
      source_id: [source.id],
      status: "new",
      only_unstructured: true,
      group_size: processing.group_size,
      limit: 100,
    });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={triggerClassName}
          title="Schedule, policy, and audit"
        >
          <SlidersHorizontal />
          Ops
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col overflow-hidden sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle>{source.display_name}</SheetTitle>
          <SheetDescription>
            Schedule, scrape policy, and recent external-site touches.
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {operationsQ.isLoading ? (
            <div className="py-10 text-center text-sm text-muted-fg">
              Loading operations…
            </div>
          ) : operationsQ.isError ? (
            <div className="py-10 text-center text-sm text-red-400">
              Failed to load operations: {(operationsQ.error as Error).message}
            </div>
          ) : operationsQ.data && policy && processing ? (
            <OperationsBody
              operations={operationsQ.data}
              cadence={cadence}
              setCadence={setCadence}
              runtimeMode={runtimeMode}
              setRuntimeMode={setRuntimeMode}
              runtimeConfig={runtimeConfig}
              setRuntimeConfig={setRuntimeConfig}
              policy={policy}
              setPolicy={setPolicy}
              processing={processing}
              setProcessing={setProcessing}
            />
          ) : null}
        </div>

        <SheetFooter className="gap-2 border-t border-border pt-4">
          <Button
            size="sm"
            variant="outline"
            onClick={runNow}
            disabled={runSource.isPending}
            title="Capture this source now through the content-scraper worker"
          >
            <Play />
            Run now
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={analyzeExisting}
            disabled={!processing || analyzeSource.isPending}
            title="Analyze existing new, unstructured inbox rows for this source"
          >
            <Sparkles />
            Analyze inbox
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={clear}
            disabled={clearCaptured.isPending}
            title="Delete captured inbox content for this source"
          >
            <Trash2 />
            Clear captured
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={sync}
            disabled={syncWorker.isPending}
            title="Push this DB source config to the content-scraper worker"
          >
            <RefreshCw />
            Sync worker
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={!policy || !processing || updateOperations.isPending}
          >
            Save
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

interface OperationsBodyProps {
  operations: SourceOperations;
  cadence: string;
  setCadence: (value: string) => void;
  runtimeMode: SourceRuntimeMode;
  setRuntimeMode: (value: SourceRuntimeMode) => void;
  runtimeConfig: SourceRuntime["config"];
  setRuntimeConfig: (value: SourceRuntime["config"]) => void;
  policy: ScrapePolicy;
  setPolicy: (value: ScrapePolicy) => void;
  processing: SourceProcessing;
  setProcessing: (value: SourceProcessing) => void;
}

function OperationsBody({
  operations,
  cadence,
  setCadence,
  runtimeMode,
  setRuntimeMode,
  runtimeConfig,
  setRuntimeConfig,
  policy,
  setPolicy,
  processing,
  setProcessing,
}: OperationsBodyProps) {
  const setPolicyField = <K extends keyof ScrapePolicy>(
    key: K,
    value: ScrapePolicy[K],
  ) => setPolicy({ ...policy, [key]: value });
  const setProcessingField = <K extends keyof SourceProcessing>(
    key: K,
    value: SourceProcessing[K],
  ) => setProcessing({ ...processing, [key]: value });
  const setRuntimeConfigField = (
    key: keyof SourceRuntime["config"],
    value: string | number,
  ) => setRuntimeConfig({ ...runtimeConfig, [key]: value });

  return (
    <div className="space-y-6 py-4">
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Bot className="size-4" />
          Scrape runtime
        </div>
        <Field label="Mode">
          <Select
            value={runtimeMode}
            onValueChange={(value) => setRuntimeMode(value as SourceRuntimeMode)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {operations.runtime.supported_modes.map((option) => (
                <SelectItem key={option} value={option}>
                  {runtimeModeLabel(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Observer strategy">
            <Select
              value={String(runtimeConfig.observer_strategy ?? "hybrid")}
              onValueChange={(value) => setRuntimeConfigField("observer_strategy", value)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="network">Network</SelectItem>
                <SelectItem value="dom">DOM</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <NumberField
            label="Watchdog seconds"
            value={configNumber(runtimeConfig.watchdog_interval_seconds, 30)}
            min={5}
            onChange={(v) => setRuntimeConfigField("watchdog_interval_seconds", v)}
          />
          <NumberField
            label="Reconcile seconds"
            value={configNumber(runtimeConfig.reconcile_interval_seconds, 300)}
            min={0}
            onChange={(v) => setRuntimeConfigField("reconcile_interval_seconds", v)}
          />
          <NumberField
            label="Scheduled max pages"
            value={configNumber(runtimeConfig.max_pages, 1)}
            min={1}
            onChange={(v) => setRuntimeConfigField("max_pages", v)}
          />
          <NumberField
            label="Browser restart minutes"
            value={configNumber(runtimeConfig.browser_restart_interval_minutes, 240)}
            min={0}
            onChange={(v) => setRuntimeConfigField("browser_restart_interval_minutes", v)}
          />
        </div>
        <div className="space-y-1 text-xs text-muted-fg">
          <RuntimePath label="Browser state" value={operations.runtime.browser_state_dir} />
          {operations.runtime.storage_state_path ? (
            <RuntimePath label="Storage state" value={operations.runtime.storage_state_path} />
          ) : null}
          {operations.runtime.chrome_profile_dir ? (
            <RuntimePath label="Chrome profile" value={operations.runtime.chrome_profile_dir} />
          ) : null}
          {operations.runtime.notes.map((note) => (
            <div key={note}>{note}</div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <RefreshCw className="size-4" />
          Content scraper worker
        </div>
        <div className="rounded-md border border-border bg-muted/10 p-3 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={operations.worker.configured ? "success" : "outline"}>
              {operations.worker.configured ? "synced" : "not synced"}
            </Badge>
            <Badge variant={operations.worker.scheduled ? "success" : "outline"}>
              {operations.worker.scheduled ? "scheduled" : "not scheduled"}
            </Badge>
            {operations.worker.running ? <Badge variant="info">running</Badge> : null}
            {operations.worker.paused === true ? (
              <Badge variant="outline">paused</Badge>
            ) : null}
            <span className="text-muted-fg">transport {operations.worker.transport}</span>
          </div>
          <div className="mt-3 space-y-1 text-muted-fg">
            {operations.worker.service_url ? (
              <RuntimePath label="Service URL" value={operations.worker.service_url} />
            ) : null}
            {operations.worker.source_url ? (
              <RuntimePath label="Source URL" value={operations.worker.source_url} />
            ) : null}
            {operations.worker.callback_url ? (
              <RuntimePath label="Callback" value={operations.worker.callback_url} />
            ) : null}
            {operations.worker.cadence ? (
              <div>Worker cadence: <span className="text-fg">{operations.worker.cadence}</span></div>
            ) : null}
            {operations.worker.runtime_mode ? (
              <div>Worker mode: <span className="text-fg">{operations.worker.runtime_mode}</span></div>
            ) : null}
            {operations.worker.last_run_at ? (
              <div>Worker last run: <span className="text-fg">{formatDate(operations.worker.last_run_at)}</span></div>
            ) : null}
            {operations.worker.last_error ? (
              <div className="rounded bg-red-500/10 px-2 py-1 text-red-300">{operations.worker.last_error}</div>
            ) : null}
            {operations.worker.error ? (
              <div className="rounded bg-red-500/10 px-2 py-1 text-red-300">{operations.worker.error}</div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Activity className="size-4" />
          Schedule
        </div>
        <Field label="Cadence">
          <Select value={cadence} onValueChange={setCadence}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {operations.supported_cadences.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <div className="rounded-md border border-border px-3 py-2 text-xs text-muted-fg">
          The content scraper worker owns scheduling. Save cadence changes to sync this source to the worker.
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <ShieldCheck className="size-4" />
          Scrape policy
        </div>
        <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
          <input
            type="checkbox"
            checked={policy.enabled}
            onChange={(e) => setPolicyField("enabled", e.target.checked)}
          />
          Enabled
        </label>
        <div className="grid gap-3 md:grid-cols-2">
          <NumberField
            label="Max vendor concurrency"
            value={policy.max_concurrency_per_vendor}
            min={1}
            onChange={(v) => setPolicyField("max_concurrency_per_vendor", v)}
          />
          <NumberField
            label="Vendor cooldown seconds"
            value={policy.min_seconds_between_vendor_fetches}
            min={0}
            onChange={(v) => setPolicyField("min_seconds_between_vendor_fetches", v)}
          />
          <NumberField
            label="Source cooldown seconds"
            value={policy.min_seconds_between_source_runs}
            min={0}
            onChange={(v) => setPolicyField("min_seconds_between_source_runs", v)}
          />
          <NumberField
            label="Stale lock seconds"
            value={policy.lock_stale_seconds}
            min={1}
            onChange={(v) => setPolicyField("lock_stale_seconds", v)}
          />
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Activity className="size-4" />
          Signal processing
        </div>
        <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
          <input
            type="checkbox"
            checked={processing.auto_extract}
            onChange={(e) => setProcessingField("auto_extract", e.target.checked)}
          />
          Auto-analyze new scraper data
        </label>
        <NumberField
          label="Analyze group size"
          value={processing.group_size}
          min={1}
          max={20}
          onChange={(v) => setProcessingField("group_size", v)}
        />
        <div className="rounded-md border border-border px-3 py-2 text-xs text-muted-fg">
          When enabled, newly saved inbox rows from this source are sent to the signal data processor after capture.
        </div>
      </section>

      <section className="space-y-3">
        <div className="text-sm font-medium">Recent scrape audit</div>
        <AuditRows operations={operations} />
      </section>
    </div>
  );
}

function RuntimePath({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 md:grid-cols-[110px_minmax(0,1fr)]">
      <span>{label}</span>
      <code className="truncate rounded bg-muted/50 px-1.5 py-0.5 font-mono text-[11px] text-fg">
        {value}
      </code>
    </div>
  );
}

function AuditRows({ operations }: { operations: SourceOperations }) {
  if (operations.recent_audit_events.length === 0) {
    return (
      <div className="rounded-md border border-border px-3 py-2 text-xs text-muted-fg">
        No scrape audit events yet.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {operations.recent_audit_events.map((event) => (
        <div key={event.id} className="rounded-md border border-border p-3 text-xs">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Badge variant={isSuccessStatus(event.status) ? "success" : "danger"}>
                {event.status}
              </Badge>
              <span>{event.event_type}</span>
              {event.trigger_kind ? <span className="text-muted-fg">{event.trigger_kind}</span> : null}
            </div>
            <span className="text-muted-fg">{formatDate(event.created_at)}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-muted-fg">
            <span>seen {event.items_seen ?? 0}</span>
            <span>saved {event.items_saved ?? 0}</span>
            <span>dupes {event.duplicates_skipped ?? 0}</span>
            {event.duration_ms != null ? <span>{event.duration_ms}ms</span> : null}
          </div>
          {event.error_message ? (
            <div className="mt-2 rounded bg-red-500/10 px-2 py-1 text-red-300">
              {event.error_type ? `${event.error_type}: ` : ""}
              {event.error_message}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-1 text-xs text-muted-fg">
      {label}
      {children}
    </label>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max?: number;
  onChange: (value: number) => void;
}) {
  return (
    <Field label={label}>
      <Input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </Field>
  );
}

function formatDate(value: string | null): string {
  if (!value) return "never";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function isSuccessStatus(status: string): boolean {
  return status === "success" || status === "succeeded";
}

function runtimeModeLabel(mode: SourceRuntimeMode): string {
  switch (mode) {
    case "poll":
      return "Poll";
    case "warm_context":
      return "Warm context";
    case "warm_page":
      return "Warm page";
    case "hybrid":
      return "Hybrid";
  }
}

function configNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
